/**
 * RealReturnInfoPanel Component
 * Click-triggered info panel explaining Real Rate of Return
 *
 * Uses a modal-style overlay for better readability of dense content.
 * More robust than a hover tooltip for this amount of information.
 */

import { useState, useRef, useEffect } from 'react';
import { cn } from '@/utils/cn';

export default function RealReturnInfoPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-1 text-left',
          'hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded'
        )}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="leading-tight text-xs font-medium text-gray-700">
          Real Rate
          <br />
          of Return
        </span>
        <span
          className={cn(
            'flex items-center justify-center w-4 h-4 rounded-full text-xs',
            'bg-blue-100 text-blue-600 hover:bg-blue-200'
          )}
          aria-hidden="true"
        >
          ?
        </span>
      </button>

      {/* Info panel */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 bg-black/20 z-40 md:hidden"
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div
            ref={panelRef}
            role="dialog"
            aria-labelledby="rrr-title"
            className={cn(
              // Mobile: centered modal
              'fixed inset-x-4 top-1/4 z-50 md:absolute md:inset-auto',
              // Desktop: positioned to the right
              'md:left-full md:top-0 md:ml-3',
              // Sizing
              'w-auto md:w-96',
              // Appearance
              'bg-gray-900 text-white rounded-lg shadow-xl',
              'p-5 text-sm leading-relaxed'
            )}
          >
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-2 right-2 p-1 text-gray-400 hover:text-white rounded"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <h3 id="rrr-title" className="font-semibold text-base mb-3 pr-6">
              What is &ldquo;Real Rate of Return&rdquo;?
            </h3>

            <p className="text-gray-200 mb-4">
              The real rate of return is your investment growth{' '}
              <span className="text-white font-medium">after</span> accounting for
              inflation. If your investments grow 7% and inflation is 3%, your real
              return is approximately 4%.
            </p>

            <h4 className="font-semibold mb-2">Why Three Rows?</h4>

            <p className="text-gray-200 mb-3">
              Nobody knows what future returns or inflation will be. These scenarios
              help you stress-test your plan:
            </p>

            <ul className="space-y-2 mb-4">
              <li className="flex items-start gap-3">
                <span className="font-mono text-white bg-gray-700 px-2 py-0.5 rounded text-xs whitespace-nowrap">Above Avg</span>
                <span className="text-gray-300">5% real return &mdash; favorable conditions</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono text-white bg-gray-700 px-2 py-0.5 rounded text-xs whitespace-nowrap">Average</span>
                <span className="text-gray-300">4% real return &mdash; typical long-term scenario</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-mono text-white bg-gray-700 px-2 py-0.5 rounded text-xs whitespace-nowrap">Below Avg</span>
                <span className="text-gray-300">3% real return &mdash; conservative scenario</span>
              </li>
            </ul>

            <div className="border-t border-gray-700 pt-3">
              <p className="text-gray-100">
                <span className="font-medium">The Goal:</span> Aim to be{' '}
                <span className="text-green-400 font-medium">green</span> across all
                rows. If you&apos;re well funded even at 3%, you&apos;re well-positioned if
                investment returns are lower than expected or inflation is higher than
                expected.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
